import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { invalidateAccountBalances, beginSync, endSync } from '../hooks/useAccountBalances';
import { Loader2, PlusCircle, DollarSign, Trash2 } from 'lucide-react';
import YearSelector from './YearSelector';
import ExportButton from './ExportButton';
import { useYear } from '../contexts/YearContext';

// Custom Confirmation Modal Component
function ConfirmDialog({ isOpen, onConfirm, onCancel, title, message }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
        <div className="flex space-x-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function MonthlyChecks() {
  const [checks, setChecks] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [description, setDescription] = useState('Monthly Check');
  const { selectedYear } = useYear();
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, checkId: null });

  // Memoized totals to avoid re-computing on every render / JSX reduce calls
  const totals = useMemo(() => {
    const businessPct = (plan?.allocation?.business ?? 20) / 100;
    const personalPct = (plan?.allocation?.personal ?? 55) / 100;
    const taxPct = (plan?.allocation?.taxes ?? 20) / 100;

    let income = 0, taxes = 0, business = 0, personal = 0, savings = 0;
    const custom = {};

    for (const c of checks) {
      const a = c.amount || 0;
      income += a;
      const b = a * businessPct;
      const p = a * personalPct;
      const t = (a - b) * taxPct; // est. tax liability after business
      const s = a - b - p - t;    // remainder → savings
      business += b;
      personal += p;
      taxes += t;
      savings += s;
      for (const alloc of plan?.allocation?.custom_allocations || []) {
        custom[alloc.name] = (custom[alloc.name] || 0) + a * (alloc.percentage / 100);
      }
    }
    return { income, taxes, business, personal, savings, custom };
  }, [checks, plan]);

  useEffect(() => {
    loadFinancialPlan();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      loadChecks();
    }
  }, [selectedYear]);

  const loadFinancialPlan = async () => {
    try {
      const response = await axios.get(`${API}/financial-plan`);
      setPlan(response.data);
    } catch (err) {
      console.error('Error loading financial plan:', err);
    }
  };

  const loadChecks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/income/monthly?year=${selectedYear}`);
      setChecks(response.data);
    } catch (err) {
      console.error('Error loading monthly checks:', err);
    } finally {
      setLoading(false);
    }
  };

  const logCheck = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      beginSync('Logging check');
      await axios.post(`${API}/income/monthly`, {
        amount: parseFloat(amount),
        description: description,
        date: checkDate || null
      });

      setAmount('');
      setCheckDate('');
      setDescription('Monthly Check');
      setShowForm(false);
      await loadChecks();
      invalidateAccountBalances();
      endSync({ success: true });
    } catch (err) {
      endSync({ success: false });
      console.error('Error logging monthly check:', err);
      alert('Failed to log monthly check');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteDialog = (checkId) => {
    setConfirmDialog({ isOpen: true, checkId });
  };

  const closeDeleteDialog = () => {
    setConfirmDialog({ isOpen: false, checkId: null });
  };

  const confirmDelete = async () => {
    if (!confirmDialog.checkId) return;

    beginSync('Deleting check');
    try {
      await axios.delete(`${API}/income/monthly/${confirmDialog.checkId}`);
      closeDeleteDialog();
      await loadChecks();
      invalidateAccountBalances();
      endSync({ success: true });
    } catch (err) {
      endSync({ success: false });
      console.error('Error deleting monthly check:', err);
      alert('Failed to delete monthly check');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onConfirm={confirmDelete}
        onCancel={closeDeleteDialog}
        title="Delete Monthly Check"
        message="Are you sure you want to delete this monthly check? This action cannot be undone."
      />

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:text-white">Monthly Checks</h1>
          <div className="flex items-center space-x-4">
            <YearSelector />
            {checks.length > 0 && (
              <ExportButton
                endpoint="/exports/monthly-checks.csv"
                params={{ year: selectedYear }}
                filename={`papertrail-monthly-checks-${selectedYear}.csv`}
                label="Export CSV"
                testId="export-monthly-checks-csv-btn"
              />
            )}
          </div>
        </div>
        <p className="text-gray-900 dark:text-gray-100 font-semibold">Track monthly commission checks and bonuses for {selectedYear}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Each row shows the allocation <strong>snapshot at the time that check was logged</strong> using your plan percentages on its pay date. These are historical records, not live balances.
        </p>
      </div>

      {/* Log New Check Button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center space-x-2 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
        >
          <PlusCircle className="w-5 h-5" />
          <span className="font-medium">Log New Monthly Check</span>
        </button>
      )}

      {/* Log Check Form */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Log Monthly Check</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                Check Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                Check Date (Optional - defaults to today)
              </label>
              <input
                type="date"
                value={checkDate}
                onChange={(e) => setCheckDate(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
                Description (Optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Monthly Commission Check"
                className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={logCheck}
                disabled={submitting}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {submitting ? 'Logging...' : 'Log Check'}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setAmount('');
                  setCheckDate('');
                  setDescription('Monthly Check');
                }}
                className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checks List */}
      {checks.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <DollarSign className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Monthly Checks Yet for {selectedYear}</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Click the button above to log your first monthly check</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Est. Tax Allocation ({plan?.allocation?.taxes ?? 20}%)</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {checks.map((check) => (
                  <tr key={check.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white dark:text-white">
                      {new Date(check.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {check.description || 'Monthly Check'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                      ${check.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      ${check.est_taxes.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => openDeleteDialog(check.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Delete check"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary with Full Allocation */}
      {checks.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Monthly Check Snapshots Total</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Sum of the persisted allocation splits for each monthly check at the time it was logged — <strong>not live balances</strong>.
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-1">
              📊 Allocation Snapshots at Time of Entry
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              These totals come from the <strong>split values stored on each monthly check row</strong> using the plan that was active when it was logged (current split: {plan?.allocation?.taxes ?? 20}/{plan?.allocation?.business ?? 20}/{plan?.allocation?.personal ?? 55}/{plan?.allocation?.savings ?? 5}). For live Expected Current Balances use <a href="/accounts" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100">Account Balances</a>; for YTD plan distribution use <a href="/reports" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100">Reports</a>.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Total Income Logged</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ${totals.income.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Tax Snapshot ({plan?.allocation?.taxes ?? 20}%)</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">Sum of per-check tax splits</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">
                ${totals.taxes.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Business Snapshot ({plan?.allocation?.business ?? 20}%)</p>
              <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                ${totals.business.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Personal Snapshot ({plan?.allocation?.personal ?? 55}%)</p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">
                ${totals.personal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">Savings Snapshot ({plan?.allocation?.savings ?? 5}%+)</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">Sum of per-check savings splits</p>
              <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                ${totals.savings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            {/* Custom allocations summary */}
            {plan?.allocation?.custom_allocations?.map((alloc) => (
              <div key={alloc.name} className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">{alloc.name} Snapshot ({alloc.percentage}%)</p>
                <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                  ${(totals.custom[alloc.name] || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyChecks;
